import { DateTime } from "luxon";
import React, { ReactNode } from "react";
import { useDispatch } from "react-redux";

import {
  Annotation,
  AnnotationType,
  RegionOfInterestAnnotation,
} from "custom-models/ViewerTypes";

import {
  deleteAnnotation,
  setEditedAnnotation,
  selectAnnotation,
} from "shared/actions/slideDetailActions";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";

import { ReactComponent as CountIcon } from "icons/count.svg";
import { ReactComponent as PencilIcon } from "icons/pencil.svg";
import { ReactComponent as PolygonIcon } from "icons/polygon.svg";
import { ReactComponent as RulerIcon } from "icons/ruler.svg";
import { ReactComponent as TextIcon } from "icons/text.svg";
import { AppDispatch } from "store/store";

import LinkButton from "../LinkButton";
import Spinner from "../Spinner";

interface AnnotationListEntryProps extends React.PropsWithChildren<any> {
  annotation: Annotation;
  pending: boolean;
  children?: ReactNode;
}

export interface AnnotationsListProps {
  slideDetail: SlideDetailState;
  features: FeatureFlags;
}

export default function AnnotationsList(props: AnnotationsListProps) {
  const dispatch = useDispatch<AppDispatch>();

  const { annotations, selectedAnnotation, pendingAnnotationsBuffer } =
    props.slideDetail;

  /*
    NOT calling stopPropagation() on EDIT and DELETE links below, so that the annotation becomes the selected annotation
    (but only transiently in the case of DELETE, before deletion completes and clears the selection)
     */
  function AnnotationListEntry(entryProps: AnnotationListEntryProps) {
    const { annotation, pending } = entryProps;
    return (
      <>
        <span className="Chat__step">{entryProps.children}</span>
        <span className="spacer" />
        {!pending && (
          <>
            {[
              AnnotationType.TEXT,
              AnnotationType.AREA_COUNT,
              // Backend is erroring: remove REGION_OF_INTEREST from edit (for now)
              // AnnotationType.REGION_OF_INTEREST,
            ].includes(annotation.type) && (
              <LinkButton
                onClick={(_event: React.MouseEvent<HTMLAnchorElement>) => {
                  // _event.stopPropagation();
                  if (
                    props.slideDetail.id !== undefined &&
                    annotation.id !== undefined
                  ) {
                    dispatch(setEditedAnnotation(annotation));
                  }
                }}
              >
                EDIT
              </LinkButton>
            )}
            <LinkButton
              onClick={(_event: React.MouseEvent<HTMLAnchorElement>) => {
                if (
                  props.slideDetail.id !== undefined &&
                  annotation.id !== undefined
                ) {
                  dispatch(
                    deleteAnnotation(props.slideDetail.id, annotation.id)
                  );
                }
                // _event.stopPropagation();
              }}
            >
              DELETE
            </LinkButton>
          </>
        )}
      </>
    );
  }

  function InfoLine(infoLineProps: {
    annotation: Annotation;
    pending?: boolean;
  }) {
    const { annotation, pending = false } = infoLineProps;
    let descriptor = "";
    if (annotation.type === AnnotationType.REGION_OF_INTEREST) {
      const roiAnnotation = annotation as RegionOfInterestAnnotation;
      descriptor = `${roiAnnotation.lesionLineType || "NO OVERLAY"}`;
      if (roiAnnotation.subtype) {
        descriptor += ` (${roiAnnotation.subtype})`;
      }
    }
    return (
      <div className="info_line">
        <AnnotationListEntry annotation={annotation} pending={pending}>
          {annotation.type === AnnotationType.MEASUREMENT && (
            <>
              <RulerIcon title="Measurement" className="svg-icon" />
              MEASUREMENT
            </>
          )}
          {annotation.type === AnnotationType.PEN_DRAWING && (
            <>
              <PencilIcon title="Drawing" className="svg-icon" />
              DRAWING
            </>
          )}
          {annotation.type === AnnotationType.TEXT && (
            <>
              <TextIcon title="Text" className="svg-icon" />
              TEXT
            </>
          )}
          {annotation.type === AnnotationType.AREA_COUNT && (
            <>
              <CountIcon title="Area Count" className="svg-icon" />
              AREA COUNT
            </>
          )}
          {annotation.type === AnnotationType.REGION_OF_INTEREST && (
            <>
              <PolygonIcon title="Region of Interest" className="svg-icon" />
              {`ROI: ${descriptor}`}
            </>
          )}
        </AnnotationListEntry>
      </div>
    );
  }

  return (
    <>
      {(annotations || [])
        .filter(
          (e: Annotation) =>
            e.type !== AnnotationType.REGION_OF_INTEREST ||
            props.features.prospectiveAI
        )
        .map((annotation) => (
          <article
            key={`annotation_${annotation.id}`}
            className={
              annotation === selectedAnnotation ? "selected_annotation" : ""
            }
            onClick={() => {
              dispatch(
                selectAnnotation(
                  annotation === selectedAnnotation ? undefined : annotation
                )
              );
            }}
          >
            <cite>
              <span>
                ID: <strong>{annotation.id}</strong>
              </span>
              <span className="header_right">
                <strong>{annotation.name}</strong>
              </span>
            </cite>
            <cite>
              {annotation.user?.first_name}&nbsp;{annotation.user?.last_name}
              {annotation.created_at ? (
                <time>
                  {DateTime.fromISO(annotation.created_at).toLocaleString()}
                </time>
              ) : (
                ""
              )}
            </cite>
            <InfoLine annotation={annotation} />
          </article>
        ))}
      {pendingAnnotationsBuffer.map((annotation: Annotation, index: number) => (
        <article key={`pending-${index}`} className="pending_annotation">
          <Spinner />
          <cite>
            <span>ID:</span>
          </cite>
          <cite>
            <span>&nbsp;</span>
            {annotation.created_at && (
              <time>
                {DateTime.fromISO(annotation.created_at).toLocaleString()}
              </time>
            )}
          </cite>
          <InfoLine pending annotation={annotation} />
        </article>
      ))}
    </>
  );

  // dispatch(deleteAnnotation(slideDetail.id, annotation.id, auth.access.token))
}
